import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

interface VersionResponse {
    version: string;
    timestamp: string; // This is actually a numeric timestamp as string
}

@Injectable({
    providedIn: 'root'
})
export class VersionCheckService {
    private serverVersionUrl = '';
    private isChecking = false;
    private storageKey = 'appVersionTimestamp';

    /** Whether the update banner should be visible */
    readonly showUpdateBar = signal(false);

    constructor(
        private http: HttpClient
    ) { }

    /**
     * Start polling `version.json` for a new deployment.
     *
     * @param frequency Poll interval in milliseconds.
     */
    public initVersionCheck(versionUrl: string, frequency: number = 1000 * 60 * 5): void {
        if (!versionUrl) {
            console.error('Version check initialized without URL');
            return;
        }

        this.serverVersionUrl = versionUrl;

        this.checkVersion();

        setInterval(() => this.checkVersion(), frequency);
    }

    private checkVersion(): void {
        if (this.isChecking || this.showUpdateBar() || !this.serverVersionUrl) {
            return;
        }

        this.isChecking = true;

        const httpOptions = {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        };

        const url = `${this.serverVersionUrl}?t=${Date.now()}`;

        this.http.get<VersionResponse>(url, httpOptions)
            .subscribe({
                next: (response) => this.handleVersionResponse(response),
                error: (err) => {
                    console.error('Could not get server version:', err);
                    this.isChecking = false;
                }
            });
    }

    private handleVersionResponse(response: VersionResponse): void {
        this.isChecking = false;

        if (!response?.timestamp) {
            console.error('Invalid version response:', response);
            return;
        }

        const storedValue = sessionStorage.getItem(this.storageKey);

        // First time this session (no stored value) — silently store, no popup
        if (!storedValue) {
            this.updateStoredVersion(response.timestamp);
            return;
        }

        const timestampChanged = this.hasTimestampChanged(response.timestamp);

        if (timestampChanged) {
            this.updateStoredVersion(response.timestamp);
            this.showUpdateDialog();
        }
    }

    private showUpdateDialog(): void {
        this.showUpdateBar.set(true);
    }

    /** Reload the page after clearing stored version */
    reloadApp(): void {
        sessionStorage.removeItem(this.storageKey);
        // Navigating with a cache-buster rather than reload(): reload() can serve a stale
        // index.html from disk cache, still referencing the old JS/CSS bundles.
        const url = new URL(window.location.href);
        url.searchParams.set("_v", Date.now().toString());
        window.location.href = url.toString();
    }

    /** Dismiss the update banner */
    dismissUpdateBar(): void {
        this.showUpdateBar.set(false);
    }

    private updateStoredVersion(timestamp: string): void {
        try {
            sessionStorage.setItem(this.storageKey, timestamp);
        } catch (e) {
            console.error('Failed to update stored version:', e);
        }
    }

    private hasTimestampChanged(serverTimestamp: string): boolean {
        const storedValue = sessionStorage.getItem(this.storageKey);
        if (!storedValue) {
            return false;
        }
        return storedValue !== serverTimestamp;
    }
}